import * as React from "react";
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  InputLabel,
  Link,
  MenuItem,
  Select,
  styled,
  TextField,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { Specialities } from "../cognito";
import { ACTION, CATEGORY, evt } from "../common/analytics";
import {
  TERMS_OF_USE_URL,
  PRIVACY_POLICY_URL,
  COOKIE_POLICY_URL,
  KAHUN_PLATFORM,
} from "./constants";
import { authenticateUser, signUp } from "../cognito";
import VerificationCode from "./verificationCode.tsx";
import PasswordInput from "./passwordInput.tsx";
import ErrorMessage from "./ErrorMessage.tsx";

//region [[ Styles ]]
const TermConfirmText = styled("div")({
  fontSize: "0.88em",
  lineHeight: 1.2,
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface SignUpFormProps {
  onSignUpComplete: (email?: string) => void;
  onError?: (error: string) => void;
  children?: React.ReactNode;
  requireFullName?: boolean;
  requireSpecialty?: boolean;
  requireTermsConfirm?: boolean;
  partner?: string;
  customFields?: Record<string, string>;
  customClientData?: Record<string, string>;
  showErrors?: boolean;
  trackEvents?: boolean;
  platform?: KAHUN_PLATFORM;
  clinic?: string;
  locale?: string;
}
//endregion [[ Props ]]

export const SignUpForm = ({
  onSignUpComplete,
  onError,
  requireFullName,
  requireTermsConfirm = true,
  requireSpecialty,
  customClientData = {},
  showErrors = true,
  trackEvents = true,
  ...props
}: SignUpFormProps) => {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [termsConfirmed, setTermsConfirmed] = useState(false);
  const [invalidPassword, setInvalidPassword] = useState(false);
  const [verificationRequired, setVerificationRequired] = useState(false);
  const [specialty, setSpecialty] = useState("");
  const [authenticating, setAuthenticating] = useState(false);
  const [error, setError] = useState("");
  const platform = props.platform || KAHUN_PLATFORM.UNKNOWN;

  const signUpDisabled =
    !email ||
    !password ||
    invalidPassword ||
    (requireSpecialty && !specialty) ||
    (requireTermsConfirm && !termsConfirmed);

  const handleError = useCallback(
    (error: any) => {
      setError(error);
      onError && onError(error);
    },
    [onError],
  );

  const resetError = () => {
    handleError("");
  };

  const onSignUp = async () => {
    try {
      setAuthenticating(true);
      await signUp(
        email,
        password,
        specialty,
        props.partner || platform,
        {
          given_name: firstName,
          family_name: lastName,
          ...(props.customFields || {}),
        },
        customClientData,
      );
      if (trackEvents) {
        evt({
          category: CATEGORY.USER_AUTHENTICATION,
          action: ACTION.SIGN_UP,
          label: email,
          props: {
            platform: props.partner || platform,
            clinic: props.clinic,
            locale: props.locale,
            userId: email,
          },
        });
      }
      resetError();
      if (customClientData.autoVerify !== "true") {
        setVerificationRequired(true);
      }
    } catch (e) {
      handleError(e as string);
      setAuthenticating(false);
    }
  };

  useEffect(() => {
    if (authenticating) {
      const timer = setInterval(async () => {
        try {
          if (email && password) {
            await authenticateUser(email, password);
            onSignUpComplete(email);
            setAuthenticating(false);
          }
        } catch (e) {
          console.error(e);
        }
      }, 3000);
      return () => clearInterval(timer);
    }
  }, [authenticating, email, onSignUpComplete, password]);

  const handleEmailChanged = (e) => {
    setEmail(e.target.value);
    resetError();
  };

  const handleFirstNameChanged = (e) => setFirstName(e.target.value);

  const handleLastNameChanged = (e) => setLastName(e.target.value);

  const handleSpecialityChanged = (v) => setSpecialty(v.target.value as string);

  const handleTermsConfirmedChange = (e) => {
    setTermsConfirmed(e.target.checked);
  };

  return (
    <>
      {!verificationRequired && (
        <>
          <TextField
            className={"email-input"}
            fullWidth
            required
            margin="normal"
            variant="outlined"
            label="Email Address"
            type="email"
            onChange={handleEmailChanged}
          />
          {requireFullName && (
            <>
              <TextField
                fullWidth
                required
                margin="normal"
                variant="outlined"
                label="First Name"
                type="string"
                onChange={handleFirstNameChanged}
              />
              <TextField
                fullWidth
                required
                margin="normal"
                variant="outlined"
                label="Last Name"
                type="string"
                onChange={handleLastNameChanged}
              />
            </>
          )}
          {requireSpecialty && (
            <div className={"speciality-input"}>
              <InputLabel>Speciality</InputLabel>
              <Select
                required
                fullWidth
                variant="outlined"
                onChange={handleSpecialityChanged}
                label="Speciality"
                value={specialty}
              >
                {Object.values(Specialities).map((s) => (
                  <MenuItem value={s} key={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </div>
          )}
          <PasswordInput
            onChange={(newVal) => setPassword(newVal)}
            onInvalid={(invalid) => setInvalidPassword(invalid)}
          />
          {requireTermsConfirm && (
            <FormControlLabel
              color={"primary"}
              control={
                <Checkbox
                  checked={termsConfirmed}
                  onChange={handleTermsConfirmedChange}
                  name="confirmTerms"
                  className={"terms-confirm-checkbox"}
                  color={"primary"}
                />
              }
              label={
                <TermConfirmText>
                  I have read and accepted the&nbsp;
                  <Link color="primary" target="_blank" href={TERMS_OF_USE_URL}>
                    Terms of use
                  </Link>
                  ,&nbsp;
                  <Link
                    color="primary"
                    target="_blank"
                    href={PRIVACY_POLICY_URL}
                  >
                    Privacy Policy
                  </Link>
                  &nbsp;and&nbsp;
                  <Link
                    color="primary"
                    target="_blank"
                    href={COOKIE_POLICY_URL}
                  >
                    Cookie Policy
                  </Link>
                </TermConfirmText>
              }
            />
          )}
          <Button
            color="primary"
            variant="contained"
            disabled={signUpDisabled || authenticating}
            fullWidth
            onClick={onSignUp}
            className="submit-button"
            endIcon={authenticating && <CircularProgress size={20} />}
          >
            Sign Up
          </Button>
        </>
      )}
      {verificationRequired && (
        <VerificationCode
          onVerificationComplete={onSignUpComplete}
          onError={handleError}
          email={email}
        />
      )}
      {error && showErrors && <ErrorMessage error={error} />}
      {props.children}
    </>
  );
};
